import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

interface AppliedFiltersState {
  count: number
  clearFilters: () => void
}

interface AppliedFiltersContextProps extends AppliedFiltersState {
  hasApplied: boolean
  register: (state: AppliedFiltersState) => void
  unregister: () => void
}

const EMPTY_STATE: AppliedFiltersState = {
  count: 0,
  clearFilters: () => undefined,
}

const AppliedFiltersContext = createContext<AppliedFiltersContextProps | undefined>(undefined)

export const AppliedFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppliedFiltersState>(EMPTY_STATE)

  const register = useCallback((next: AppliedFiltersState) => setState(next), [])
  const unregister = useCallback(() => setState(EMPTY_STATE), [])

  return (
    <AppliedFiltersContext.Provider
      value={{
        ...state,
        hasApplied: state.count > 0,
        register,
        unregister,
      }}
    >
      {children}
    </AppliedFiltersContext.Provider>
  )
}

const BOUNDARY_VALUE: AppliedFiltersContextProps = {
  ...EMPTY_STATE,
  hasApplied: false,
  register: () => undefined,
  unregister: () => undefined,
}

/**
 * Скрывает применённые фильтры страницы от вложенных таблиц: внутри раскрытых
 * строк свой список, и заглушка про фильтры страницы там не к месту.
 */
export const AppliedFiltersBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AppliedFiltersContext.Provider value={BOUNDARY_VALUE}>{children}</AppliedFiltersContext.Provider>
)

/**
 * Применённые фильтры текущей страницы: сколько их и как сбросить.
 * Читается заглушками внутри таблиц, вне провайдера отдаёт пустое состояние.
 */
export const useAppliedFilters = () => {
  const context = useContext(AppliedFiltersContext)

  return {
    count: context?.count ?? 0,
    hasApplied: context?.hasApplied ?? false,
    clearFilters: context?.clearFilters ?? EMPTY_STATE.clearFilters,
  }
}

const isEmptyValue = (value: unknown) =>
  value === undefined || value === null || value === '' || value === false

export const countAppliedFilters = <T extends object>(values: T, defaults: Partial<T>) =>
  (Object.keys(defaults) as (keyof T)[]).filter(
    (field) => !isEmptyValue(values[field]) && values[field] !== defaults[field],
  ).length

/** Проставляет форме значения фильтров по умолчанию — общий помощник для onClear. */
export const resetFilterFields = <T extends FieldValues>(
  setValue: UseFormSetValue<T>,
  defaults: Partial<T>,
) => {
  Object.entries(defaults).forEach(([field, value]) => {
    setValue(field as Path<T>, value as PathValue<T, Path<T>>)
  })
}

interface RegisterAppliedFiltersParams<T extends object> {
  /** Применённые значения фильтров — то, что уже ушло в запрос. */
  values: T
  /** Значения, к которым фильтры возвращаются по Clear filters. */
  defaults: Partial<T>
  /** Сбросить фильтры формы и применить. */
  onClear: () => void
}

/**
 * Публикует применённые фильтры страницы в контекст, чтобы заглушка пустой
 * таблицы понимала, что результатов нет именно из-за фильтров, и умела их сбросить.
 */
export const useRegisterAppliedFilters = <T extends object>({
  values,
  defaults,
  onClear,
}: RegisterAppliedFiltersParams<T>) => {
  const context = useContext(AppliedFiltersContext)
  const clearRef = useRef(onClear)

  useEffect(() => {
    clearRef.current = onClear
  })

  const count = useMemo(() => countAppliedFilters(values, defaults), [values, defaults])

  const register = context?.register
  const unregister = context?.unregister

  useEffect(() => {
    register?.({ count, clearFilters: () => clearRef.current() })
  }, [count, register])

  useEffect(() => () => unregister?.(), [unregister])
}
